import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Col, Form, Row,
} from 'react-bootstrap';
import FormModal from 'components/FormModal';
import BottomSheet from 'components/DesignSystem/BottomSheet';
import Switch from 'components/DesignSystem/Switch';
import Button from 'components/DesignSystem/Button';
import Tag from 'components/DesignSystem/Tag';
import useIsMobile from 'utils/hooks/useIsMobile';
import {
  WindxPainelOption,
  WindxTela,
  WindxCategoria,
  WindxOpcaoRmv,
  WindxTipoSimulacao,
} from 'types/Windx/Models/windxPainelOption';
import {
  useCreateWindxModelMutation,
  useUpdateWindxModelMutation,
} from 'services/api/windxModels';
import { ORDEM_CATEGORIAS } from 'helper/windx';

const TELAS: WindxTela[] = ['sh', 'sm', 'comparativo', 'personalizado'];
// Mesma ordem do painel (WEOL-SM #99) — fonte unica em helper/windx, para a
// lista do admin nao divergir do agrupador.
const CATEGORIAS: readonly WindxCategoria[] = ORDEM_CATEGORIAS;
const OPCOES_RMV: WindxOpcaoRmv[] = ['com_rmv', 'sem_rmv'];
const TIPOS_SIMULACAO: WindxTipoSimulacao[] = ['preliminar', 'final'];

export interface ModelsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (wasEditing: boolean) => void;
  model: WindxPainelOption | null; // null = criar; objeto = editar
}

// Grupo de checkboxes (telas/rmv/tipo_simulacao). Usa <input> + <label htmlFor>
// (Form.Check não passa jsx-a11y/label-has-associated-control — ver memória FE).
function CheckboxGroup<T extends string>({
  legend, options, selected, onToggle, idPrefix,
}: {
  legend: string;
  options: T[];
  selected: T[];
  onToggle: (value: T) => void;
  idPrefix: string;
}) {
  return (
    <Form.Group className="mb-3">
      <Form.Label className="mb-1">{legend}</Form.Label>
      <div className="d-flex flex-wrap gap-3">
        {options.map((opt) => {
          const inputId = `${idPrefix}-${opt}`;
          return (
            <div key={opt} className="d-flex align-items-center gap-1">
              <input
                type="checkbox"
                id={inputId}
                checked={selected.includes(opt)}
                onChange={() => onToggle(opt)}
              />
              <label htmlFor={inputId} className="mb-0 small">{opt}</label>
            </div>
          );
        })}
      </div>
    </Form.Group>
  );
}

export default function ModelsModal({
  open, onClose, onSuccess, model,
}: ModelsModalProps) {
  const isEditing = model !== null;

  const [codigo, setCodigo] = useState('');
  const [label, setLabel] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [categoria, setCategoria] = useState<WindxCategoria | ''>('');
  const [ativo, setAtivo] = useState(true);
  const [telas, setTelas] = useState<WindxTela[]>([]);
  const [diasPrevisao, setDiasPrevisao] = useState(1);
  const [opcoesRmv, setOpcoesRmv] = useState<WindxOpcaoRmv[]>([]);
  const [tipoSimulacao, setTipoSimulacao] = useState<WindxTipoSimulacao[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [createModel, { isLoading: isCreating }] = useCreateWindxModelMutation();
  const [updateModel, { isLoading: isUpdating }] = useUpdateWindxModelMutation();
  const isMobile = useIsMobile();

  useEffect(() => {
    setSaveError(null);
    if (model) {
      setCodigo(model.codigo);
      setLabel(model.config.label);
      setOrdem(model.order_by);
      setCategoria(model.categoria ?? '');
      setAtivo(model.ativo);
      setTelas(model.telas);
      setDiasPrevisao(model.config.dias_previsao);
      setOpcoesRmv(model.config.opcoes_rmv);
      setTipoSimulacao(model.config.tipo_simulacao ?? []);
    } else {
      setCodigo('');
      setLabel('');
      setOrdem(0);
      setCategoria('');
      setAtivo(true);
      setTelas([]);
      setDiasPrevisao(1);
      setOpcoesRmv([]);
      setTipoSimulacao([]);
    }
  }, [model]);

  const toggle = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
  ) => (value: T) => {
      setter((prev) => (prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]));
    };

  const handleSubmit = useCallback(async () => {
    const config = {
      label,
      opcoes_rmv: opcoesRmv,
      dias_previsao: diasPrevisao,
      ...(tipoSimulacao.length > 0 ? { tipo_simulacao: tipoSimulacao } : {}),
    };
    try {
      if (isEditing) {
        await updateModel({
          id: model.id,
          telas,
          categoria: categoria || null,
          order_by: ordem,
          ativo,
          config,
        }).unwrap();
      } else {
        await createModel({
          codigo,
          telas,
          categoria: categoria || null,
          order_by: ordem,
          ativo,
          config,
        }).unwrap();
      }
      onSuccess(isEditing);
      onClose();
    } catch (err) {
      // O modal permanece aberto e mostra a mensagem do backend (ex.: codigo
      // duplicado, dados invalidos). RTK Query expoe o envelope do BE em `data`.
      const e = err as { data?: { description?: string; message?: string } };
      setSaveError(
        e?.data?.description || e?.data?.message || 'Não foi possível salvar o modelo.',
      );
    }
  }, [
    isEditing, model, codigo, label, ordem, categoria, ativo, telas,
    diasPrevisao, opcoesRmv, tipoSimulacao, createModel, updateModel,
    onClose, onSuccess,
  ]);

  const isLoading = isCreating || isUpdating;
  const canSubmit = Boolean(
    codigo && label && telas.length > 0 && opcoesRmv.length > 0 && diasPrevisao >= 1,
  );

  const title = isEditing ? 'Editar Modelo' : 'Novo Modelo';

  const body = (
    <div>
      {saveError && (
      <Alert variant="danger" className="py-2 small" data-testid="windx-modelo-save-error">
        {saveError}
      </Alert>
      )}
      {!isEditing && (
      <Alert variant="warning" className="py-2 small">
        Cadastrar aqui registra o modelo no painel; os dados só aparecem após
        o ETL (windx-automatico) ser configurado para gerá-los — coordene com
        o Renan.
      </Alert>
      )}
      {isEditing && (
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="text-muted">{`ID: ${model.id}`}</span>
        <Tag
          type={ativo ? 'completed' : 'cancelled'}
          icon={ativo ? 'checkmark' : 'close'}
        >
          {ativo ? 'Ativo' : 'Inativo'}
        </Tag>
      </div>
      )}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="windxModelCodigo">
            <Form.Label>Código</Form.Label>
            <Form.Control
              type="text"
              value={codigo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCodigo(e.target.value)}
              disabled={isEditing}
            />
            <Form.Text className="text-muted">
              {isEditing ? 'Imutável (chave do ETL)' : 'Identificador único (ex.: GEM)'}
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="windxModelLabel">
            <Form.Label>Label (exibido no painel)</Form.Label>
            <Form.Control
              type="text"
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="windxModelOrdem">
            <Form.Label>Ordem</Form.Label>
            <Form.Control
              type="number"
              value={ordem}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setOrdem(Number(e.target.value));
              }}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="windxModelDias">
            <Form.Label>Dias de previsão</Form.Label>
            <Form.Control
              type="number"
              value={diasPrevisao}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiasPrevisao(Number(e.target.value));
              }}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="windxModelCategoria">
            <Form.Label>Categoria</Form.Label>
            <Form.Select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as WindxCategoria | '')}
            >
              <option value="">(nenhuma)</option>
              {CATEGORIAS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <CheckboxGroup
        legend="Telas"
        options={TELAS}
        selected={telas}
        onToggle={toggle(setTelas)}
        idPrefix="windx-modelo-tela"
      />
      <CheckboxGroup
        legend="Opções de RMV"
        options={OPCOES_RMV}
        selected={opcoesRmv}
        onToggle={toggle(setOpcoesRmv)}
        idPrefix="windx-modelo-rmv"
      />
      <CheckboxGroup
        legend="Tipo de simulação"
        options={TIPOS_SIMULACAO}
        selected={tipoSimulacao}
        onToggle={toggle(setTipoSimulacao)}
        idPrefix="windx-modelo-tiposim"
      />

      <Form.Group className="d-flex align-items-center gap-3 mb-3">
        <Form.Label className="mb-0">Ativo (exibir no painel)</Form.Label>
        <Switch
          className={isMobile ? 'ds-switch-pill' : ''}
          checked={ativo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAtivo(e.target.checked)}
        />
      </Form.Group>
    </div>
  );

  const footer = (
    <div className="d-flex justify-content-end">
      <Button
        icon={isEditing ? 'checkmark' : 'add'}
        onClick={handleSubmit}
        disabled={isLoading || !canSubmit}
      >
        {isEditing ? 'Salvar' : 'Criar'}
      </Button>
    </div>
  );

  // No mobile o formulário sobe de baixo num BottomSheet (mesmo padrão de
  // Usuários/Empresas); no desktop segue no FormModal centralizado.
  if (isMobile) {
    return (
      <BottomSheet open={open} dismiss={onClose} title={title}>
        {body}
        {footer}
      </BottomSheet>
    );
  }

  return (
    <FormModal show={open} title={title} onHide={onClose} lastAsFooter>
      {body}
      {footer}
    </FormModal>
  );
}
